// src/pages/NotFoundPage.tsx

import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Unplug, ArrowLeft, AlertCircle } from 'lucide-react';
import { motion, Variants } from 'framer-motion';
import styles from '../css/Global.module.css';

const NotFoundPage: FC = () => {
    
    // Staggered entrance for the content
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { 
                staggerChildren: 0.1,
                delayChildren: 0.1
            }
        }
    };

    // Item slide-up
    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
        visible: { 
            opacity: 1, 
            y: 0, 
            filter: 'blur(0px)',
            transition: { duration: 0.5, ease: "easeOut" } 
        }
    };

    // Button interaction variants
    const buttonVariants: Variants = {
        initial: { scale: 1, backgroundColor: "var(--zinc-900)" },
        hover: { 
            scale: 1.02, 
            backgroundColor: "#000000",
            transition: { duration: 0.2 }
        },
        tap: { 
            scale: 0.95,
            backgroundColor: "var(--zinc-800)",
            transition: { duration: 0.1 }
        }
    };

    const iconVariants: Variants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { 
            scale: 1, 
            opacity: 1, 
            transition: { 
                type: "spring", 
                stiffness: 200, 
                damping: 20 
            } 
        }
    };

    return (
        <div className={styles.notFoundPage}>
            {/* Technical Grid Background */}
            <div className={styles.gridBackground}></div>
            
            <div className={styles.container}>
                <motion.div 
                    className={styles.contentWrapper}
                    variants={containerVariants} 
                    initial="hidden" 
                    animate="visible"
                >
                    <motion.div className={styles.iconWrapper} variants={iconVariants}>
                        <div className={styles.iconPulse}></div>
                        <Unplug size={48} strokeWidth={1.5} className={styles.mainIcon} />
                        <div className={styles.statusBadge}>
                            <AlertCircle size={14} /> Error 404
                        </div>
                    </motion.div>
                    
                    <motion.h1 className={styles.notFoundTitle} variants={itemVariants}>
                        Signal <span className={styles.titleHighlight}>Lost.</span>
                    </motion.h1>
                    
                    <motion.p className={styles.notFoundText} variants={itemVariants}>
                        The coordinate you are attempting to access does not exist within the OTA TECH directory. The page may have been moved or deprecated.
                    </motion.p>
                    
                    <motion.div variants={itemVariants}>
                        <Link to="/" className={styles.linkWrapper}>
                            <motion.button 
                                className={styles.btnPrimary}
                                variants={buttonVariants}
                                initial="initial"
                                whileHover="hover"
                                whileTap="tap"
                                whileFocus="hover"
                            >
                                <ArrowLeft size={18} /> Return to Base
                            </motion.button>
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
            
            {/* Fade Effect at bottom */}
            <div className={styles.bottomFade}></div>
        </div>
    );
};

export default NotFoundPage;